/**
 * Presenting Sample Connection statuses. Pure — no React, no fetching.
 *
 * One status per connection, from the Platform manifest. A profile existing is
 * not "Connected"; only a provider that answered a live check is, and this
 * module keeps every other state visibly different from that one.
 */
import {
  PROVIDER_ORDER as PROFILE_PROVIDER_ORDER,
  PROVIDER_LABELS as PROFILE_PROVIDER_LABELS,
  describeProvider,
  describeSecret,
  describeUnavailable,
} from '../connections/connectionProfilePresentation';
import { describeEnvironment } from './environmentPresentation';

/** StatusBadge tone key for a status (its lower-cased code, mapped in TONE_MAP). */
export function badgeStatusFor(status) {
  return `sample_${String(status || 'unknown').toLowerCase()}`;
}

/* ==========================================================================
 * PRE-CREATE PREFLIGHT — what a tenant would be given if Demo Connections is
 * left on, read before the tenant exists.
 *
 * Everything below is pure. It answers one question: an operator is about to
 * press Create with Demo Connections ON — what will actually be attached, and
 * what is already broken about it?
 *
 * THE SOURCE. Before creation there is no tenant and therefore no manifest.
 * The one thing that CAN be read is the environment's profile:
 *
 *     GET /api/admin/sample-connection-profiles?environment=<non-LIVE>
 *       -> { profiles: [ { id, environment, label, kind, status,
 *                          desired_items: [ { provider, connection, label,
 *                                             credential_imported_at_selection } ],
 *                          desired_item_count, profile_fingerprint,
 *                          providers: { <key>: { enabled, status, config,
 *                                                secret:{present,backend,name},
 *                                                last_validated_at, last_error,
 *                                                connections:[{key,label}] } } } ] }
 *       -> or { profiles: [], unavailable, detail }
 *
 * WHICH OF THOSE PROFILES. Not this module's choice. The caller passes
 * `defaultProfileId` — `default_sample_profile_id` from the environment
 * registry, which is the same function `AdminService.create_tenant` resolves
 * the create request with — and the profile previewed is the one carrying that
 * id. Taking `profiles[0]` instead was a second chooser, and the two could
 * disagree without a word on screen. See `describeDemoConnections`.
 *
 * THE ROSTER IS `desired_items`, AND IS NOT RECOMPUTED HERE. That field is
 * produced by `demo_profile_intent.desired_items_from_platform_profile` — the
 * SAME flattener the approval freeze runs — so the list shown before Create and
 * the list frozen at approval are one definition. Walking `providers` here to
 * rebuild it would be a second flattener on the frontend, free to disagree
 * with the first: the hard-coded five connection names again, one refactor
 * further along. `providers` is still read, for ONE thing — the provider's own
 * last check, joined onto a row the server put there.
 *
 * ABSENT IS NOT EMPTY. No `desired_items` field is its own state
 * (`items_not_reported`), never a short list and never a zero.
 *
 * NO FIFTH STATE IS ADDED. Three vocabularies already exist and each is the
 * backend's:
 *
 *   profile provider `status`   NOT_CONFIGURED PENDING CONNECTED DEGRADED
 *                               EXPIRED REAUTH_REQUIRED FAILED
 *   row `status`                draft | validated | invalid
 *                                 (sample_connections._ROW_STATUS)
 *   lifecycle / health          draft|active|inactive, healthy|attention|
 *                               no_test|unknown
 *                                 (integration_service.derive_lifecycle/health)
 *
 * The first is what the profile knows today; the second is the row the attach
 * writes; the third is how every other Cluvik surface reads that row. They are
 * related here, in one place, by transcription — not by invention.
 *
 * WHY THE FOUR-STAGE READINESS MODEL IS NOT RENDERED. SUPPORTED ->
 * SECRET_AVAILABLE -> PROVIDER_VERIFIED -> CLUVIK_E2E_VERIFIED is the canonical
 * model and it is the right one, but NO ENDPOINT EMITS IT. It exists as a
 * hand-measured table in DEMO_CONNECTIONS_RECONCILIATION.md §7 and in no code
 * on either side of the wire. Deriving it here from `status` would be a
 * fabrication with the shape of a measurement: `status: CONNECTED` is the
 * provider answering a probe, which is PROVIDER_VERIFIED at best and says
 * nothing at all about Cluvik's own path — and S6 in DEMO_CONNECTIONS_FINDINGS
 * is a live example of exactly that gap (an S3 profile imported with static
 * keys probes CONNECTED while Cluvik's only S3 client path refuses it). So the
 * stages are absent from this screen until something serves them, and their
 * absence is stated in the UI rather than filled in.
 * ========================================================================== */


/**
 * Purpose groups, in reading order. A DISPLAY grouping keyed on the provider
 * slug — it is not served, and it is not a status.
 *
 * Four of the six have no member in today's catalogue, and they are declared
 * anyway. An operator who switches Demo Connections on and does not see Slack
 * is owed an answer, and "the profile covers none of Communications" is that
 * answer; silently listing only what exists lets the screen read as complete.
 */
export const DECLARED_PURPOSES = ['ai', 'productivity', 'storage', 'communications', 'developer', 'platform'];

/** `other` is a landing place, never a declared purpose — see `purposeForConnection`. */
export const PURPOSE_ORDER = [...DECLARED_PURPOSES, 'other'];

/**
 * The operator-facing category names, from the product owner's own list.
 *
 * The KEY is an internal slug and the LABEL is what an operator reads. They are
 * allowed to differ — `productivity` is the slug this build has always used and
 * "Email & Calendar" is what the category is called — and renaming the slug
 * would move a join key for a cosmetic reason.
 */
export const PURPOSE_LABELS = {
  ai: 'AI',
  productivity: 'Email & Calendar',
  storage: 'Storage',
  communications: 'Communications',
  developer: 'Developer & Collaboration',
  platform: 'Platform',
  other: 'Other',
};

/**
 * What each declared category is FOR, in one phrase. Used only to orient a
 * reader looking at a category this profile has no member of.
 *
 * NOT A ROSTER AND NOT A PROMISE — read the difference carefully, because the
 * first draft of this got it wrong. It said "Would carry OpenAI / Cluvik AI",
 * which reads as "coming soon", and the truth is colder: the demo catalogue
 * (`sample_connections.PROVIDERS`) is a CLOSED ENUMERATION and today it holds
 * three providers and five connections —
 *
 *     google_workspace  google_email_calendar, google_drive_storage
 *     microsoft_365     microsoft_365_email_calendar, microsoft_365_storage
 *     aws_s3            aws_s3_storage
 *
 * — so four of the six categories below have no member anywhere, not merely
 * none in this profile. Nothing here is loading and nothing is pending.
 * Read directly from the catalogue rather than taken on report.
 *
 * WHY THIS CANNOT GO QUIETLY STALE. These phrases render ONLY for a category
 * with zero SERVED items. The day a credential for one lands in a profile, the
 * category has members, is rendered from the served list like any other, and
 * this entry is not reached at all. It can be out of date only about a category
 * that is empty, which is the one case where "what this is for" is all there is
 * to say.
 *
 * SLACK IS DELIBERATELY NOT NAMED. It is a target the owner explicitly held
 * back (`provider_readiness.OWNER_STANDARD_DEMO_TARGETS`, standard_demo=False),
 * so listing it beside GitHub and Teams would render a decision as an absence.
 * Held back and not built are different facts, and this screen has no served
 * source for the first — so it says neither.
 */
export const PURPOSE_MEMBERS = {
  ai: 'OpenAI / Cluvik AI',
  productivity: 'Google Workspace, Microsoft Outlook',
  storage: 'Google Drive, OneDrive / SharePoint, Amazon S3',
  communications: 'Twilio, WhatsApp',
  developer: 'GitHub, Microsoft Teams',
  platform: 'Google Maps',
};

/**
 * Connection slug -> purpose. The five slugs the demo catalogue ships today
 * (`sample_connections.PROVIDERS`) are first; the rest are provider slugs that
 * exist in `integration_providers` but are NOT in the demo catalogue
 * (DEMO_CONNECTIONS_RECONCILIATION.md §7). They are mapped ahead of time so
 * that a catalogue addition lands in its group rather than in Other — which is
 * the only reason to write them down. Their presence here does not put them in
 * a profile and nothing in this module claims it does.
 */
const CONNECTION_PURPOSE = {
  google_email_calendar: 'productivity',
  microsoft_365_email_calendar: 'productivity',
  google_drive_storage: 'storage',
  microsoft_365_storage: 'storage',
  aws_s3_storage: 'storage',
  // Not in the demo catalogue today.
  openai: 'ai',
  twilio: 'communications',
  meta_whatsapp: 'communications',
  // Slack and Teams sit under Developer & Collaboration, not Communications —
  // the product owner's grouping. Twilio and WhatsApp are how a tenant talks to
  // ITS customers; Slack and Teams are how a team talks to itself.
  slack: 'developer',
  teams: 'developer',
  github: 'developer',
  google_maps: 'platform',
};

/**
 * The purpose a connection belongs to. An unmapped slug goes to `other` and is
 * still rendered.
 *
 * The tempting shape here is a filter over the known slugs, and it is wrong: a
 * connection this build has not heard of would then vanish from a screen whose
 * entire job is to say what the tenant is about to receive. Landing in Other is
 * visibly un-grouped, which is the correct amount of alarming.
 */
export function purposeForConnection(slug) {
  return CONNECTION_PURPOSE[String(slug || '')] || 'other';
}

/* ── the three vocabularies, related by transcription ───────────────────── */

/**
 * Profile provider status -> the `tenant_connections.status` the attached row
 * is created with. Transcribed from `sample_connections._ROW_STATUS`.
 *
 * Unknown resolves to `draft`, exactly as the Platform's `row_status_for` does,
 * and for its stated reason: a status this build has never heard of is not
 * evidence that a provider passed.
 */
const ROW_STATUS = {
  CONNECTED: 'validated',
  NOT_CONFIGURED: 'draft',
  PENDING: 'draft',
  DEGRADED: 'invalid',
  EXPIRED: 'invalid',
  REAUTH_REQUIRED: 'invalid',
  FAILED: 'invalid',
};

export function rowStatusForProfileStatus(status) {
  return ROW_STATUS[String(status || '').toUpperCase()] || 'draft';
}

/** Row status -> lifecycle. Transcribed from `IntegrationService._LIFECYCLE_BUCKET`. */
const LIFECYCLE_BUCKET = {
  draft: 'draft', invalid: 'draft', validated: 'active', active: 'active', disabled: 'inactive',
};

export function lifecycleForRowStatus(rowStatus) {
  return LIFECYCLE_BUCKET[String(rowStatus || '')] || 'draft';
}

/**
 * Profile provider status -> health, in `derive_health`'s vocabulary.
 *
 * `no_test` and `unknown` are kept apart because the backend keeps them apart:
 * a credential nobody has measured (PENDING, NOT_CONFIGURED) is no_test, and a
 * status word this build cannot read is unknown. Neither is healthy. Absence of
 * measurement is not health — that comment is `derive_health`'s own.
 */
const HEALTH_BY_STATUS = {
  CONNECTED: 'healthy',
  DEGRADED: 'attention',
  EXPIRED: 'attention',
  REAUTH_REQUIRED: 'attention',
  FAILED: 'attention',
  PENDING: 'no_test',
  NOT_CONFIGURED: 'no_test',
};

export function healthForProfileStatus(status) {
  return HEALTH_BY_STATUS[String(status || '').toUpperCase()] || 'unknown';
}

export const LIFECYCLE_LABELS = { draft: 'Draft', active: 'Active', inactive: 'Inactive' };
export const HEALTH_LABELS = {
  healthy: 'Healthy', attention: 'Needs attention', no_test: 'Not tested', unknown: 'Unknown',
};

/* ── the served roster ──────────────────────────────────────────────────── */

/**
 * Display order for provider keys: the catalogue first, then anything else the
 * runtime named. It orders rows and NOTHING ELSE — membership comes from the
 * server's `desired_items` and a provider absent from that list does not appear
 * because this function has never heard of it.
 */
function providerRank(key) {
  const at = PROFILE_PROVIDER_ORDER.indexOf(String(key || ''));
  return at === -1 ? PROFILE_PROVIDER_ORDER.length : at;
}

/**
 * One row per SERVED desired item. THE ROSTER IS NOT COMPUTED HERE.
 *
 * `profiles[].desired_items` is produced by `demo_profile_intent
 * .desired_items_from_platform_profile` — the SAME flattener the approval
 * freeze runs — so the list an operator is shown before pressing Create and the
 * list recorded in the frozen specification are one definition, not two that
 * agree until one is edited. Re-deriving the roster from `providers` here would
 * restore exactly that drift, one refactor further along than the hard-coded
 * five names it replaced.
 *
 * `null` when the field is ABSENT, never `[]`. A build that did not send the
 * list has not told us the profile is empty; it has told us nothing, and the
 * caller renders that as its own state rather than as a clean short list.
 *
 * FIELDS ARE NAMED, NEVER SPREAD. `...entry` would be shorter and would carry
 * whatever the API adds next straight into a rendered object — including, the
 * day someone widens `public_profile`, credential material. Everything this
 * screen shows is listed here by hand, and `secret` is reduced to a backend
 * name by `describeSecret` before it is allowed anywhere near a row.
 */
export function expandProfileToConnections(profile) {
  const items = profile?.desired_items;
  if (!Array.isArray(items)) return null;
  const providers = profile?.providers || {};

  const rows = items.map((item, index) => {
    const providerKey = item?.provider ?? null;
    const providerLabel = PROFILE_PROVIDER_LABELS[providerKey] || providerKey || 'Unnamed provider';
    const entry = Object.prototype.hasOwnProperty.call(providers, providerKey)
      ? (providers[providerKey] || {})
      : null;

    // TRI-STATE, from the server. `credential_imported_at_selection` is
    // `bool(enabled)` when the runtime reported `enabled` and `null` when it
    // did not — ABSENT IS NOT FALSE, and collapsing it to false here would
    // manufacture "no credential" out of a reporting gap.
    const raw = item?.credential_imported_at_selection;
    const imported = raw === true ? true : (raw === false ? false : null);

    // The provider's own last check, only where a provider block was served.
    const axes = entry ? describeProvider(entry) : null;

    let status = null;
    let credentialLabel = 'Not reported';
    if (imported === false) {
      // The runtime said there is no credential. The Platform's attach writes
      // a NOT_CONFIGURED row, and a stale CONNECTED on the entry is not
      // evidence of anything: nothing is attached, so nothing is connected.
      status = 'NOT_CONFIGURED';
      credentialLabel = 'Not configured';
    } else if (axes) {
      status = axes.status;
      credentialLabel = imported === true ? axes.lifecycleLabel : 'Credential state not reported';
    }

    // No provider block and no `false`: there is nothing measured to report,
    // and `unknown` says so. NOT_CONFIGURED here would be a claim about a
    // credential nobody looked at.
    const health = status === null ? 'unknown' : healthForProfileStatus(status);
    const rowStatus = rowStatusForProfileStatus(status);

    return {
      provider: providerKey,
      providerLabel,
      connection: item?.connection ?? null,
      // The Platform's own label for the connection, as frozen. A local
      // display name would be a second, drifting copy of a served string.
      label: item?.label || item?.connection || providerLabel,
      purpose: purposeForConnection(item?.connection),
      status,
      credentialImported: imported,
      credentialLabel,
      secretLabel: imported === true && entry ? describeSecret(entry.secret) : null,
      health,
      healthLabel: HEALTH_LABELS[health],
      rowStatus,
      lifecycle: lifecycleForRowStatus(rowStatus),
      lifecycleLabel: LIFECYCLE_LABELS[lifecycleForRowStatus(rowStatus)],
      badgeStatus: badgeStatusFor(status || 'unknown'),
      lastValidatedAt: entry?.last_validated_at || null,
      lastError: imported === false ? null : (entry?.last_error || null),
      // The desired item names a provider the profile payload did not carry.
      // Flagged rather than hidden: it is a reporting gap, not a healthy row.
      providerNotReported: entry === null,
      // Preserves the served order underneath the catalogue sort.
      servedAt: index,
    };
  });

  rows.sort((a, b) => (providerRank(a.provider) - providerRank(b.provider))
    || (a.servedAt - b.servedAt));
  return rows;
}

/**
 * Providers the runtime carried that contributed NO desired item.
 *
 * The server's flattener skips a provider with no `connections` list and one
 * whose connection has no join key, both with a logged warning. Without this
 * the profile would simply look smaller — a reporting gap rendered as a
 * shorter, healthier profile, which is the family of defect this screen
 * exists to prevent.
 */
export function providersWithoutDesiredItems(profile) {
  const items = profile?.desired_items;
  if (!Array.isArray(items)) return [];
  const named = new Set(items.map((i) => String(i?.provider)));
  return Object.keys(profile?.providers || {})
    .filter((key) => !named.has(String(key)))
    .sort((a, b) => providerRank(a) - providerRank(b))
    .map((key) => ({ provider: key, providerLabel: PROFILE_PROVIDER_LABELS[key] || key }));
}

/** Rows grouped by purpose, plus the declared purposes nothing landed in. */
export function groupConnectionsByPurpose(rows) {
  const list = rows || [];
  const groups = PURPOSE_ORDER
    .map((key) => ({ key, label: PURPOSE_LABELS[key], rows: list.filter((r) => r.purpose === key) }))
    .filter((g) => g.rows.length > 0);
  const present = new Set(groups.map((g) => g.key));
  const absent = DECLARED_PURPOSES
    .filter((key) => !present.has(key))
    .map((key) => ({ key, label: PURPOSE_LABELS[key], members: PURPOSE_MEMBERS[key] }));
  return { groups, absent };
}

/* ── required failure vs optional warning ───────────────────────────────── */

/**
 * THE DISTINCTION THIS SCREEN MUST NOT FLATTEN.
 *
 * `blocking` — the profile cannot be applied at all. `attach_profile_to_tenant`
 * raises before its first write (`profile_not_found`, or a preflight finding),
 * so NOTHING is attached and the operator's Demo Connections request is not
 * honoured in any part.
 *
 * `warnings` — the profile applies and one provider inside it is unusable. The
 * other connections are attached and work.
 *
 * Deliberately NOT phrased as "provisioning will fail". S2 of
 * DEMO_CONNECTIONS_FINDINGS records that the Demo Connections step is
 * `required=False` in every verification branch today, so a failed attach still
 * produces `overall: complete` and a READY tenant. Whether that is fixed is
 * another lane's work; either way what this screen states is the ATTACH
 * outcome, which is true before and after that fix.
 *
 * `unknown` is a third tier and not a shade of the other two. A profile we
 * could not read is not a profile with no problems — which is the whole failure
 * this area exists to make visible.
 */
const HEALTH_WARNING_TEXT = {
  attention: 'The provider rejected this credential when it was last checked. This connection attaches, and will not work.',
  no_test: 'A credential exists and has never been checked. It attaches as a draft connection.',
  unknown: 'The runtime reported a status this build does not recognise. Treated as unmeasured, not as working.',
};

/**
 * The whole panel state, from the fetch's three possible outcomes.
 *
 * @param {object}  args
 * @param {boolean} args.allowed      Environment may carry demo connections.
 * @param {boolean} args.requested    The operator switched Demo Connections on.
 * @param {boolean} args.loading      A request is in flight.
 * @param {string}  args.error        A failed request, already reduced to text.
 * @param {object}  args.payload      The API body, when one arrived.
 * @param {string}  args.environment  Selected environment code.
 * @param {string}  args.defaultProfileId  The profile id creation will resolve
 *   for this environment — `default_sample_profile_id` from the environment
 *   registry. The one profile previewed is the one carrying it. Absent means
 *   the registry was not read, which is reported as unknown, never guessed.
 */
export function describeDemoConnections({
  allowed, requested, loading, error, payload, environment, defaultProfileId,
} = {}) {
  const env = String(environment || '').toUpperCase();
  // The operator's word for the environment, from the module that owns it —
  // `env.toLowerCase()` produced "for demo" and "the development runtime",
  // which read as prose rather than as the name of a place.
  const envLabel = describeEnvironment(env).label;
  const base = {
    environment: env, profile: null, rows: [], groups: [], absent: [],
    blocking: [], warnings: [], counts: null, silentProviders: [],
  };

  if (!allowed) {
    return {
      ...base,
      state: 'not_allowed',
      severity: 'ok',
      headline: `Demo Connections are not available for ${envLabel}`,
      detail: 'Shared Cluvik credentials never attach to a Live tenant. The Platform, the Admin route and the profile store each refuse it.',
    };
  }
  if (!requested) {
    return {
      ...base,
      state: 'off',
      severity: 'ok',
      headline: 'Demo Connections are off',
      detail: 'This tenant will be created with no provider connections. Sample Records is a separate switch and is unaffected by this one.',
    };
  }
  if (loading) {
    return {
      ...base,
      state: 'loading',
      severity: 'unknown',
      headline: 'Checking the demo connection profile…',
      detail: `Reading the ${envLabel} runtime.`,
    };
  }
  if (error) {
    // NOT an empty catalogue. A failed request rendering as "nothing
    // configured" is the exact defect this programme exists to close.
    return {
      ...base,
      state: 'error',
      severity: 'unknown',
      headline: 'The demo connection profile could not be read',
      detail: String(error),
      blocking: [{
        code: 'read_failed',
        title: 'Readiness is unknown, not clear',
        detail: 'Nothing was measured. Creating the tenant now will still attempt the attach, and this screen cannot say what it would find.',
      }],
    };
  }

  const unavailable = describeUnavailable(payload);
  if (unavailable) {
    return {
      ...base,
      state: 'unavailable',
      severity: 'unknown',
      headline: `The ${envLabel} runtime could not be reached`,
      detail: `${unavailable}. This is not the same as having no profile — nothing was measured.`,
      blocking: [{
        code: 'runtime_unreachable',
        title: 'Readiness is unknown, not clear',
        detail: 'The runtime that would perform the attach did not answer. Nothing on this screen can be read as a pass.',
      }],
    };
  }

  const profiles = payload?.profiles || [];
  /**
   * THE PROFILE THIS SCREEN PREVIEWS IS THE PROFILE THAT WILL BE ATTACHED,
   * because both are the same id read from the same place.
   *
   * There used to be two choosers. This screen took `profiles[0]` — the
   * runtime's first entry, relayed in the runtime's own order by
   * GET /api/admin/sample-connection-profiles, which sorts nothing — while the
   * SERVER resolved the profile a second time at create:
   * `AdminService.create_tenant` sets `sample_connection_profile_id` to
   * `default_sample_profile_id(environment)` whenever the operator left it
   * unset. Nothing made the two agree, and where the runtime held more than
   * one profile an operator could approve one preflight and get another
   * profile, silently.
   *
   * `defaultProfileId` closes it. It is `default_sample_profile_id` off the
   * environment registry (`GET /api/admin/environments`), which the server
   * computes with the SAME function it resolves the create request with. The
   * screen no longer picks; it looks up the id the server has already
   * published, in the list the runtime returned.
   *
   * WHY THE SCREEN STILL SENDS NO `sample_connection_profile_id`. The create
   * request does accept one (admin_models.py:221) and it is trusted, not
   * validated: nothing between the request model and
   * `sample_connections.attach_profile_to_tenant` checks that the profile's
   * environment is the tenant's — only that neither is LIVE. Sending an id
   * from here would make this screen the author of an unvalidated field for no
   * gain, since the id it would send is the one the server already resolves.
   * Looking one up is not the same as choosing one.
   *
   * NOT KNOWING IS A STATE. If the registry has not been read there is no id
   * to look up, and falling back to `profiles[0]` would restore the defect
   * exactly. That case reports `unknown`, like every other case here that did
   * not measure.
   */
  const wantedProfileId = String(defaultProfileId || '').trim();
  if (!wantedProfileId) {
    return {
      ...base,
      state: 'profile_undetermined',
      severity: 'unknown',
      headline: 'Which demo connection profile this tenant would be given is not known',
      detail: `The environment registry has not been read, so the profile id that creating a ${envLabel} tenant resolves to is unknown. The runtime listed ${profiles.length} profile${profiles.length === 1 ? '' : 's'}; choosing one of them here is exactly what this screen must not do.`,
      blocking: [{
        code: 'profile_undetermined',
        title: 'Readiness is unknown, not clear',
        detail: 'Nothing below has been measured against the profile that would actually be attached. Re-check, or reload the screen so the environment registry can be read.',
      }],
    };
  }
  const profile = profiles.find((p) => p && p.id === wantedProfileId) || null;
  if (!profile) {
    return {
      ...base,
      state: 'no_profile',
      severity: 'blocked',
      headline: `The ${envLabel} runtime does not have the profile this tenant would be given`,
      // NO SUBSTITUTE. Another profile being available is not a reason to show
      // it: the server will still resolve the one named here, and the attach
      // will still refuse.
      detail: profiles.length
        ? `Creating this tenant resolves ${wantedProfileId}. The runtime listed ${profiles.length} profile${profiles.length === 1 ? '' : 's'} and none of them is it.`
        : `Creating this tenant resolves ${wantedProfileId}, and the ${envLabel} runtime lists no profile at all.`,
      blocking: [{
        code: 'profile_not_found',
        title: 'This profile cannot be applied',
        detail: 'The attach step refuses before its first write, so no connection at all is created for this tenant.',
      }],
    };
  }

  const rows = expandProfileToConnections(profile);
  if (rows === null) {
    // The profile was read and did not carry `desired_items`. Deriving the
    // roster from `providers` here would put a second flattener on the
    // frontend, free to disagree with the one the freeze runs — which is the
    // hard-coded five names again, one refactor further along.
    return {
      ...base,
      state: 'items_not_reported',
      severity: 'unknown',
      profile,
      headline: 'The runtime did not say what this profile would attach',
      detail: 'The profile exists and its connection list was not reported. Nothing here can say how many connections this tenant would receive.',
      blocking: [{
        code: 'desired_items_absent',
        title: 'Readiness is unknown, not clear',
        detail: 'Creating the tenant now will still attempt the attach, and this screen cannot say what it would find.',
      }],
    };
  }

  const { groups, absent } = groupConnectionsByPurpose(rows);
  const counts = countByHealth(rows);
  const warnings = [];
  for (const row of rows) {
    if (row.providerNotReported) {
      warnings.push({
        code: 'provider_not_reported',
        connection: row.label,
        provider: row.providerLabel,
        title: `${row.label} — the provider was not described`,
        detail: 'This connection is in the profile and the runtime sent nothing about the provider behind it. Whether a credential exists was not measured, and is not claimed here.',
      });
      continue;
    }
    if (row.credentialImported === false) {
      warnings.push({
        code: 'not_configured',
        connection: row.label,
        provider: row.providerLabel,
        title: `${row.label} has no credential`,
        // The Platform's own reasoning, in `summarize`: Demo Connections ON
        // promises every connection, so a provider nobody imported is a gap.
        detail: 'No credential has been imported for this provider. The connection is not created, and anything that needs it stays unconfigured.',
      });
      continue;
    }
    if (row.credentialImported === null) {
      warnings.push({
        code: 'credential_state_not_reported',
        connection: row.label,
        provider: row.providerLabel,
        title: `${row.label} — credential state not reported`,
        detail: 'The runtime did not say whether a credential is imported for this provider. Absent is not "none": the provider check below is shown, and the import state is not guessed from it.',
      });
      continue;
    }
    if (row.health !== 'healthy') {
      warnings.push({
        code: row.health,
        connection: row.label,
        provider: row.providerLabel,
        title: `${row.label} — ${row.healthLabel.toLowerCase()}`,
        detail: row.lastError || HEALTH_WARNING_TEXT[row.health],
      });
    }
  }

  // A provider the runtime carried that named no connection. Raised here
  // rather than invented as a row: the roster is the served one, and a gap in
  // it is a gap, not an extra member.
  const silentProviders = providersWithoutDesiredItems(profile);
  for (const sp of silentProviders) {
    warnings.push({
      code: 'provider_named_no_connection',
      connection: sp.providerLabel,
      provider: sp.providerLabel,
      title: `${sp.providerLabel} contributes nothing to this profile`,
      detail: 'The runtime carried this provider and named no connection under it, so nothing from it is in the list above. That is a reporting gap, not an empty provider.',
    });
  }

  // The endpoint's own count, checked against its own list. They cannot
  // disagree unless the server is inconsistent with itself, and a screen that
  // silently picks one of two disagreeing numbers is worse than one that says
  // they disagree.
  const servedCount = profile?.desired_item_count;
  const countMismatch = Number.isInteger(servedCount) && servedCount !== rows.length;
  if (countMismatch) {
    warnings.push({
      code: 'served_count_mismatch',
      connection: null,
      provider: null,
      title: 'The runtime’s own count and its own list disagree',
      detail: `desired_item_count is ${servedCount} and ${rows.length} item${rows.length === 1 ? ' was' : 's were'} listed. Neither number is treated as the answer.`,
    });
  }

  const allMissing = rows.length > 0 && rows.every((r) => r.credentialImported === false);
  const severity = warnings.length ? 'warning' : 'ok';
  return {
    ...base,
    state: allMissing ? 'nothing_enabled' : 'ready',
    severity,
    profile,
    rows,
    groups,
    absent,
    counts,
    warnings,
    silentProviders,
    headline: allMissing
      ? 'The profile exists and carries no credentials'
      : countMismatch
        ? 'The runtime disagreed with itself about how many connections this profile holds'
        : `${counts.total} connection${counts.total === 1 ? '' : 's'} will be attached`,
    detail: summarizeReadiness(counts),
  };
}

/** Counts on the health axis. Never a score: `3 of 5` reads as a mark out of
 *  the profile, and a connection nobody configured did not fail a test. */
export function countByHealth(rows) {
  const list = rows || [];
  const count = (h) => list.filter((r) => r.health === h).length;
  return {
    total: list.length,
    healthy: count('healthy'),
    attention: count('attention'),
    no_test: count('no_test'),
    unknown: count('unknown'),
    // Only a served `true` counts as configured. `null` is a reporting gap
    // and must not be added to either side of this tally.
    configured: list.filter((r) => r.credentialImported === true).length,
  };
}

/** One sentence over the counts, in the health vocabulary. */
export function summarizeReadiness(counts) {
  if (!counts || !counts.total) return 'Nothing to attach';
  const parts = [];
  if (counts.healthy) parts.push(`${counts.healthy} healthy`);
  if (counts.attention) parts.push(`${counts.attention} needing attention`);
  if (counts.no_test) parts.push(`${counts.no_test} not tested`);
  if (counts.unknown) parts.push(`${counts.unknown} unknown`);
  return parts.join(', ');
}
